import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  AvatarGroup,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Build,
  BuildCircle,
  Assignment,
  Group,
  Schedule as ScheduleIcon,
  LocationOn,
  AccessTime,
} from '@mui/icons-material';
import axios from 'axios';
import { mockApi } from '../../services/mockData';
import ScheduleForm from '../ScheduleForm/ScheduleForm';

interface ScheduleItem {
  id: string;
  type: 'installation' | 'maintenance' | 'inspection' | 'meeting' | 'training';
  title: string;
  description: string;
  customer: string;
  customerId: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'rescheduled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  scheduledDate: string;
  startTime: string;
  endTime: string;
  estimatedDuration: number;
  actualDuration: number;
  location: string;
  region: string;
  assignedTo: string;
  team: string[];
  notes: string;
  recurring: boolean;
  recurringPattern?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  weatherDependent: boolean;
  specialRequirements: string[];
  createdAt: string;
  updatedAt: string;
}

interface SharedCalendarProps {
  open: boolean;
  onClose: () => void;
  onDateSelect: (date: Date) => void;
  onAddSchedule: (date: Date) => void;
  selectedDate?: Date;
  title?: string;
}

const SharedCalendar: React.FC<SharedCalendarProps> = ({
  open,
  onClose,
  onDateSelect,
  onAddSchedule,
  selectedDate = new Date(),
  title = "Select Date"
}) => {
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [scheduleFormOpen, setScheduleFormOpen] = useState(false);
  const [clickedDate, setClickedDate] = useState<Date | null>(null);

  const fetchScheduleItems = useCallback(async () => {
    try {
      setLoading(true);
      const schedules = await mockApi.getSchedules();
      setScheduleItems(schedules as unknown as ScheduleItem[]);
    } catch (err: any) {
      console.error('Failed to fetch schedule items:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchScheduleItems();
    }
  }, [open, fetchScheduleItems]);

  const getRegionColor = (region: string): string => {
    const colors = {
      'North': '#4CAF50',
      'South': '#2196F3',
      'East': '#FF9800',
      'West': '#9C27B0',
      'Central': '#F44336',
      'Unassigned': '#757575',
    };
    return colors[region as keyof typeof colors] || '#757575';
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return <Build />;
      case 'maintenance': return <BuildCircle />;
      case 'inspection': return <Assignment />;
      case 'meeting': return <Group />;
      case 'training': return <ScheduleIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'rescheduled': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getSchedulesForDate = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return scheduleItems.filter(item => item.scheduledDate === dateString);
  };

  const getCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const formatTime = (time: string) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleDateClick = (date: Date) => {
    setClickedDate(date);
    setScheduleFormOpen(true);
  };

  const handleAddSchedule = (date: Date) => {
    onAddSchedule(date);
    onClose();
  };

  const handleScheduleFormClose = () => {
    setScheduleFormOpen(false);
    setClickedDate(null);
  };

  const handleScheduleCreated = (schedule: any) => {
    // Refresh the schedule items
    fetchScheduleItems();
    // Call the parent's onAddSchedule callback
    onAddSchedule(clickedDate || new Date());
  };

  const handlePreviousMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#D4AF37' }}>
            {title}
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              onClick={handlePreviousMonth}
              size="small"
              sx={{ color: '#D4AF37' }}
            >
              ←
            </Button>
            <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
              {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </Typography>
            <Button
              onClick={handleNextMonth}
              size="small"
              sx={{ color: '#D4AF37' }}
            >
              →
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Calendar Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 2 }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <Typography key={day} variant="subtitle2" sx={{ textAlign: 'center', color: '#D4AF37', fontWeight: 'bold', p: 1 }}>
                {day}
              </Typography>
            ))}
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {getCalendarDays(calendarDate).map((day, index) => {
              const isCurrentMonth = day.getMonth() === calendarDate.getMonth();
              const isToday = day.toDateString() === new Date().toDateString();
              const daySchedules = getSchedulesForDate(day);
              const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
              
              return (
                <Box
                  key={index}
                  onClick={() => isCurrentMonth && handleDateClick(day)}
                  sx={{
                    minHeight: 100,
                    border: '1px solid #333333',
                    borderRadius: 1,
                    p: 1,
                    cursor: isCurrentMonth ? 'pointer' : 'default',
                    backgroundColor: isCurrentMonth ? '#1A1A1A' : '#0A0A0A',
                    opacity: isCurrentMonth ? 1 : 0.3,
                    position: 'relative',
                    '&:hover': isCurrentMonth ? {
                      backgroundColor: '#2A2A2A',
                      border: '2px solid #D4AF37',
                    } : {},
                    ...(isSelected && {
                      border: '2px solid #D4AF37',
                      backgroundColor: '#2A2A2A',
                    }),
                    ...(isToday && {
                      border: '2px solid #4CAF50',
                    }),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: isCurrentMonth ? '#FFFFFF' : '#666666',
                      fontWeight: isToday ? 'bold' : 'normal',
                      mb: 0.5,
                    }}
                  >
                    {day.getDate()}
                  </Typography>
                  
                  {/* Schedule indicators */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {daySchedules.slice(0, 2).map((schedule, scheduleIndex) => (
                      <Tooltip
                        key={scheduleIndex}
                        title={
                          <Box>
                            <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                              {schedule.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#AAAAAA' }}>
                              {schedule.customer} • {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#AAAAAA', display: 'block' }}>
                              {schedule.region} • {schedule.type}
                            </Typography>
                          </Box>
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            p: 0.5,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderRadius: 0.5,
                            border: `1px solid ${getRegionColor(schedule.region || 'Unassigned')}`,
                            cursor: 'pointer',
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: getRegionColor(schedule.region || 'Unassigned'),
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#FFFFFF',
                              fontSize: '0.65rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '80px',
                            }}
                          >
                            {schedule.title}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                    
                    {daySchedules.length > 2 && (
                      <Typography variant="caption" sx={{ color: '#AAAAAA', fontSize: '0.6rem', textAlign: 'center' }}>
                        +{daySchedules.length - 2} more
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          
          {/* Legend */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#1A1A1A', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#D4AF37' }}>
              Region Legend:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {['North', 'South', 'East', 'West', 'Central', 'Unassigned'].map((region) => (
                <Box key={region} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: getRegionColor(region),
                      border: '1px solid #FFFFFF',
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#AAAAAA' }}>
                    {region}
                  </Typography>
                </Box>
              ))}
            </Box>
            
            {/* Type Legend */}
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#D4AF37' }}>
              Schedule Types:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                { type: 'installation', label: 'Installation', icon: <Build /> },
                { type: 'maintenance', label: 'Maintenance', icon: <BuildCircle /> },
                { type: 'inspection', label: 'Inspection', icon: <Assignment /> },
                { type: 'meeting', label: 'Meeting', icon: <Group /> },
                { type: 'training', label: 'Training', icon: <ScheduleIcon /> },
              ].map(({ type, label, icon }) => (
                <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: '#D4AF37', fontSize: '1rem' }}>{icon}</Box>
                  <Typography variant="caption" sx={{ color: '#AAAAAA' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={() => handleAddSchedule(selectedDate)}
          variant="contained"
          startIcon={<Add />}
          sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
        >
          Add Schedule
        </Button>
      </DialogActions>

      {/* Schedule Form Dialog */}
      {clickedDate && (
        <ScheduleForm
          open={scheduleFormOpen}
          onClose={handleScheduleFormClose}
          selectedDate={clickedDate}
          onScheduleCreated={handleScheduleCreated}
        />
      )}
    </Dialog>
  );
};

export default SharedCalendar;
